/**
 * STATISTICS' SHARED PARTS (design 18; the owner, 2026-10-05: "its bad shaped on UI/UX and
 * components" - thirteen equal tiles became one chart with its headline figures as switches, a
 * ranked list and a list of the rest). Like ./kit.tsx they decide no wording, fetch nothing and know
 * no route: a screen hands them words, numbers and nodes.
 *
 *   FigureSwitch  the headline figures as buttons over one chart - Ghost's KPI tabs, Plausible's
 *                 top stats: the label, the period's number and its change; the pressed one is
 *                 the figure the chart and the list beside it show (`aria-pressed`, one group named
 *                 for what it chooses). Five across from a 600px row, three across below it. A
 *                 figure's (i) (design 24, T15) sits beside its button in the same cell, never
 *                 inside it: a button cannot hold another.
 *   StatCard      a titled card - "Most read in these 30 days" - with an optional way onward.
 *   BarList       ranked rows with a bar BEHIND the words (Plausible's breakdowns): the bar is a
 *                 drawing of the number printed at the right, its length against the first row's,
 *                 so it says nothing the number does not; the words keep their contrast.
 *   FigureList    label / number / change rows - "Also in these 30 days".
 */
import { Badge } from '@mantine/core';
import { useId } from 'react';
import type { CSSProperties, ReactNode } from 'react';
import { ChangeNote } from './change-note';
import type { StatDelta } from './kit';
import classes from './figures.module.css';

/* --------------------------------------------------------------------------- figure switch */

export interface SwitchFigure<K extends string> {
  readonly key: K;
  readonly label: string;
  /** A mark before the label - the coin beside "Countable views". Decoration only. */
  readonly icon?: ReactNode;
  /** A badge after the label - "Beta" (`FigureBadge`). Part of the button's name. */
  readonly badge?: string | undefined;
  /** The period's figure, already worded. */
  readonly value: ReactNode;
  /** True when `value` is words ("Not measured yet") rather than a number. */
  readonly quiet?: boolean;
  readonly change?: StatDelta | undefined;
  /** What the figure means - an (i) the screen words and draws - beside the button, not in it. */
  readonly info?: ReactNode;
}

export interface FigureSwitchProps<K extends string> {
  /** What the buttons choose, for a screen reader: "Show on the chart". */
  readonly label: string;
  readonly figures: readonly SwitchFigure<K>[];
  readonly pressed: K;
  readonly onPress: (key: K) => void;
}

export function FigureSwitch<K extends string>({
  label,
  figures,
  pressed,
  onPress,
}: FigureSwitchProps<K>) {
  return (
    <div className={classes.switchRoom}>
      <div
        role="group"
        aria-label={label}
        className={classes.switch}
        style={{ '--figure-count': String(figures.length) } as CSSProperties}
      >
        {figures.map((figure) => (
          <div
            key={figure.key}
            className={classes.switchCell}
            data-pressed={figure.key === pressed ? 'true' : undefined}
          >
            <button
              type="button"
              className={classes.switchFigure}
              aria-pressed={figure.key === pressed}
              onClick={() => {
                onPress(figure.key);
              }}
            >
              <span className={classes.switchLabel}>
                <span className={classes.switchName}>
                  {figure.icon}
                  {figure.label}
                </span>
                {/* The space keeps the button's name "Countable views Beta", not one word. */}
                {figure.badge === undefined ? null : (
                  <>
                    {' '}
                    <FigureBadge>{figure.badge}</FigureBadge>
                  </>
                )}
              </span>
              <span className={figure.quiet === true ? classes.switchQuiet : classes.switchValue}>
                {figure.value}
              </span>
              <span className={classes.switchChange}>
                {figure.change === undefined ? null : <ChangeNote change={figure.change} />}
              </span>
            </button>
            {figure.info === undefined ? null : (
              <span className={classes.switchInfo}>{figure.info}</span>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

/**
 * The small badge beside a figure's name - "Beta". A `span`, because it sits inside buttons (the
 * switch, a sortable column's header) and a button may hold phrasing content only.
 */
export function FigureBadge({ children }: { readonly children: ReactNode }) {
  return (
    <Badge
      component="span"
      size="xs"
      variant="light"
      color="moon"
      radius="sm"
      className={classes.figureBadge}
    >
      {children}
    </Badge>
  );
}

/* -------------------------------------------------------------------------------- stat card */

export interface StatCardProps {
  readonly title: string;
  /** The way onward at the title's right - "All articles". */
  readonly action?: ReactNode;
  readonly children: ReactNode;
}

export function StatCard({ title, action, children }: StatCardProps) {
  const titleId = useId();
  return (
    <section className={classes.card} aria-labelledby={titleId}>
      <div className={classes.cardHead}>
        <h3 className={classes.cardTitle} id={titleId}>
          {title}
        </h3>
        {action === undefined ? null : <span className={classes.cardAction}>{action}</span>}
      </div>
      {children}
    </section>
  );
}

/* --------------------------------------------------------------------------------- bar list */

export interface ListedBar {
  readonly key: string;
  /** The row's words - a title, often a link. */
  readonly label: ReactNode;
  /** The number, worded: "1,204 views". */
  readonly value: string;
  /** The bar's length, 0..1, against the longest on screen. */
  readonly share: number;
}

export function BarList({ bars, label }: { readonly bars: readonly ListedBar[]; label: string }) {
  return (
    <ul className={classes.bars} aria-label={label}>
      {bars.map((bar) => (
        <li key={bar.key} className={classes.bar}>
          <span
            aria-hidden="true"
            className={classes.barFill}
            style={{ width: `${String(Math.max(2, Math.min(1, bar.share) * 100))}%` }}
          />
          <span className={classes.barLabel}>{bar.label}</span>
          <span className={classes.barValue}>{bar.value}</span>
        </li>
      ))}
    </ul>
  );
}

/* ------------------------------------------------------------------------------ figure list */

export interface ListedFigure {
  readonly label: string;
  readonly value: ReactNode;
  readonly quiet?: boolean;
  readonly change?: StatDelta | undefined;
  /** One short line under the label: "every view, ever", "+3 new, 1 lost", a link. */
  readonly note?: ReactNode;
}

export function FigureList({ figures }: { readonly figures: readonly ListedFigure[] }) {
  return (
    <dl className={classes.list}>
      {figures.map((figure) => (
        <div key={figure.label} className={classes.listRow}>
          <dt className={classes.listLabel}>
            {figure.label}
            {figure.note === undefined ? null : (
              <span className={classes.listNote}>{figure.note}</span>
            )}
          </dt>
          <dd className={classes.listFigure}>
            <span className={figure.quiet === true ? classes.listQuiet : classes.listValue}>
              {figure.value}
            </span>
            {figure.change === undefined ? null : <ChangeNote change={figure.change} />}
          </dd>
        </div>
      ))}
    </dl>
  );
}
